import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(customParseFormat);


export const firstValue = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

export const personName = (person) => {
  if (!person) return null;
  if (typeof person === "string") return person;
  const full = firstValue(person, ["full_name", "name", "username"]);
  if (full) return full;
  const parts = [person.first_name, person.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : null;
};

export const titleCase = (value) => {
  const text = String(value).trim();
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Some endpoints send Django-rendered display strings such as
// "August 26, 2026, 2:55 p.m." / "Aug. 26, 2026, 2:55 p.m." / "... noon",
// which are not ISO and so cannot be parsed directly.
const DISPLAY_FORMATS = [
  "MMMM D, YYYY, h:mm a",
  "MMM D, YYYY, h:mm a",
  "MMMM D, YYYY h:mm a",
  "MMM D, YYYY h:mm a",
  "MMMM D, YYYY",
  "MMM D, YYYY",
];

const normalizeDisplayStamp = (value) =>
  value
    .replace(/\ba\.m\.?/gi, "am")
    .replace(/\bp\.m\.?/gi, "pm")
    .replace(/\bmidnight\b/gi, "12:00 am")
    .replace(/\bnoon\b/gi, "12:00 pm")
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const parseStamp = (value) => {
  if (value instanceof Date) return dayjs(value);
  const text = String(value).trim();
  if (!text) return null;

  // ISO-ish values keep the repo's existing UTC-as-displayed convention.
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const iso = dayjs.utc(text);
    return iso.isValid() ? iso : null;
  }

  const normalized = normalizeDisplayStamp(text);
  for (const format of DISPLAY_FORMATS) {
    const parsed = dayjs(normalized, format, true);
    if (parsed.isValid()) return parsed;
  }

  const loose = dayjs(normalized);
  return loose.isValid() ? loose : null;
};

export const formatHistoryStamp = (value) => {
  if (!value) return null;
  const parsed = parseStamp(value);
  return parsed ? parsed.format("DD MMM YYYY, hh:mm a") : null;
};

const buildEntry = (title, by, at) => ({
  title,
  by: by || "-",
  at: at || "-",
  // Only the "generated" entry gets the highlighted border.
  isGenerated: /^generated$/i.test(title),
});

export const normalizeHistoryEntry = (entry, index) => {
  if (!entry || typeof entry !== "object") return null;

  const at = formatHistoryStamp(
    firstValue(entry, [
      "datetime",
      "at",
      "created_at",
      "updated_at",
      "modified_at",
      "timestamp",
      "date",
    ])
  );
  const by = personName(
    firstValue(entry, [
      "user",
      "by",
      "practitioner",
      "created_by",
      "updated_by",
      "modified_by",
      "performed_by",
      "user_name",
    ])
  );
  const rawTitle = firstValue(entry, [
    "type",
    "title",
    "action",
    "event",
    "status",
  ]);

  if (!at && !by) return null;
  return buildEntry(
    rawTitle ? titleCase(rawTitle) : index === 0 ? "Generated" : "Updated",
    by,
    at
  );
};
